import React, { FC } from 'react'

import { unionBy } from 'lodash'
import { v4 } from 'uuid'
import { CirclePlus, TrashIcon } from 'lucide-react'

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select'
import { Button } from '../../ui/button'

import { getUserFullName } from '../../../helpers/helpers'
import { cn } from '../../../lib/utils'

import { IUserInterface } from '../../../models/users/user.interface'
import { IAddedTeamMembers } from '../AddClasses'

import { CONST } from '../../../const/const'

interface TeamMemberItemProps {
  disabled?: boolean
  memberId: string
  memberRole: string
  onValueChange?: (values: { [key: string]: string | Date }) => void
  onRemove?: () => void
  teamMembers: IUserInterface[]
}

const TeamMemberItem: FC<TeamMemberItemProps> = ({
  disabled,
  memberId,
  memberRole,
  onValueChange,
  onRemove,
  teamMembers,
}) => {
  return (
    <div className="relative flex flex-wrap sm:flex-nowrap gap-2 pr-10">
      <Select
        disabled={disabled}
        value={memberId || undefined}
        onValueChange={(value) => {
          const current = teamMembers.find((item) => item.id === value)
          if (onValueChange) {
            onValueChange({
              memberId: value,
              memberName: current ? getUserFullName(current) : '',
              memberPicture: current?.userProfilePicture || '',
              memberDob: current?.userDOB || '',
              memberEmail: current?.userEmail || '',
            })
          }
        }}
      >
        <SelectTrigger
          className={cn(
            'p-4 !h-auto disabled:bg-black/[3%] disabled:border-transparent rounded-md w-full sm:w-8/12',
            memberId && 'bg-black/[3%] border-transparent'
          )}
        >
          <SelectValue placeholder="Select team member" />
        </SelectTrigger>
        <SelectContent>
          {teamMembers.map((item) => (
            <SelectItem key={item.id} value={item.id}>
              {getUserFullName(item)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select
        disabled={disabled}
        value={memberRole}
        onValueChange={(value) => onValueChange && onValueChange({ memberRole: value })}
      >
        <SelectTrigger
          className={cn(
            'p-4 !h-auto disabled:bg-black/[3%] disabled:border-transparent rounded-md w-full sm:w-4/12',
            memberRole && 'bg-black/[3%] border-transparent'
          )}
        >
          <SelectValue placeholder="Role" />
        </SelectTrigger>
        <SelectContent>
          {CONST.UI.ROLES.map((role) => (
            <SelectItem
              disabled={role.value === CONST.UI.TEAM_MEMBERS_ROLES.RIDER}
              key={role.value}
              value={role.value}
            >
              {role.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Button
        disabled={disabled}
        variant="ghost"
        className="absolute z-10 top-1/2 -translate-y-1/2 right-0 p-2 rounded-full"
        onClick={onRemove}
      >
        <TrashIcon className="w-4" />
      </Button>
    </div>
  )
}

interface AddTeamMembersProps {
  user: IUserInterface
  teamMembers: IUserInterface[]
  addedTeamMembers: IAddedTeamMembers[]
  setAddedTeamMembers: (value: IAddedTeamMembers[]) => void
}

export const AddTeamMembers: FC<AddTeamMembersProps> = ({
  user,
  teamMembers,
  addedTeamMembers,
  setAddedTeamMembers,
}) => {
  const onSelectChange = (id: string, values: { [key: string]: string | Date }) => {
    const horsesUpdated = addedTeamMembers.map((item) => {
      if (item.id === id) {
        return { ...item, ...values }
      } else {
        return item
      }
    })
    setAddedTeamMembers([...horsesUpdated])
  }

  const onRemove = (id: string) => {
    setAddedTeamMembers([...addedTeamMembers.filter((item) => item.id !== id)])
  }

  return (
    <>
      <h4 className="text-lg font-normal mb-1">Add team members</h4>
      <p className="text-sm text-black/50 mb-4">
        Pegasus will automatically send team members liability paperwork to sign
      </p>
      <div className="space-y-2">
        <TeamMemberItem disabled memberId={user.id} memberRole="Rider" teamMembers={[user]} />
        {addedTeamMembers.map((member, index) => (
          <TeamMemberItem
            key={index}
            memberId={member.memberId}
            memberRole={member.memberRole || ''}
            teamMembers={unionBy([...teamMembers, user], 'id')}
            onValueChange={(values) => onSelectChange(member.id, values)}
            onRemove={() => onRemove(member.id)}
          />
        ))}
        <Button
          variant="link"
          className="text-black/50 w-full mt-3 underline hover:no-underline text-xs font-normal"
          onClick={() =>
            setAddedTeamMembers([
              ...addedTeamMembers,
              {
                id: `${v4()}${Date.now()}`,
                memberId: '',
                memberName: '',
                memberPicture: '',
                memberDob: '',
                memberEmail: '',
                memberRole: CONST.UI.TEAM_MEMBERS_ROLES.OWNER,
              },
            ])
          }
        >
          <CirclePlus className="w-3" />
          Add person
        </Button>
      </div>
    </>
  )
}
